/**
 * Section Avis Google — données 100 % dynamiques via /api/public/google-reviews.
 * Lazy-load (IntersectionObserver), fallback discret (section masquée), attributions Google conformes.
 */
import React from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function Stars({ value = 0, size = 15, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} strokeWidth={1.5}
              className={i <= Math.round(value) ? "fill-or text-or" : "text-pierre"} />
      ))}
    </span>
  );
}

function GoogleLogo({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-label="Google">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function visitLabel(vd) {
  if (!vd || !vd.year) return null;
  const month = vd.month ? MONTHS_FR[vd.month - 1] : null;
  return month ? `Séjour en ${month} ${vd.year}` : `Séjour en ${vd.year}`;
}

function ReviewCard({ r, index }) {
  const [expanded, setExpanded] = React.useState(false);
  const long = (r.text || "").length > 260;
  const visit = visitLabel(r.visit_date);
  const initial = (r.author_name || "G").trim().charAt(0).toUpperCase();
  return (
    <article className="snap-start shrink-0 w-[85%] sm:w-[70%] md:w-[calc((100%-4rem)/3)] bg-white/70 border border-pierre/70 p-6 md:p-7 flex flex-col"
             data-testid={`google-review-card-${index}`}>
      <div className="flex items-center gap-3">
        {r.author_photo_uri ? (
          <img src={r.author_photo_uri} alt="" loading="lazy" referrerPolicy="no-referrer"
               className="h-10 w-10 rounded-full object-cover"
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <span aria-hidden="true" className="h-10 w-10 rounded-full bg-domaine/10 text-domaine font-serif text-lg flex items-center justify-center">{initial}</span>
        )}
        <div className="min-w-0">
          {r.author_uri ? (
            <a href={r.author_uri} target="_blank" rel="noreferrer"
               className="block text-sm font-medium text-ardoise truncate hover:text-domaine transition-colors">
              {r.author_name}
            </a>
          ) : (
            <span className="block text-sm font-medium text-ardoise truncate">{r.author_name}</span>
          )}
          <span className="text-xs text-ardoise/55">{r.relative_time}</span>
        </div>
      </div>
      <Stars value={r.rating} size={13} className="mt-3" />
      <p className={`mt-3 text-sm leading-relaxed text-ardoise/85 whitespace-pre-line ${expanded ? "" : "line-clamp-5"}`}>
        {r.text}
      </p>
      {long && (
        <button type="button" onClick={() => setExpanded((v) => !v)}
                data-testid={`google-review-toggle-${index}`}
                className="mt-1 self-start text-xs text-domaine underline underline-offset-4 hover:text-or transition-colors">
          {expanded ? "Réduire" : "Lire la suite"}
        </button>
      )}
      <div className="mt-auto pt-4 flex items-center justify-between gap-2 text-xs">
        {visit ? <span className="text-ardoise/55 italic">{visit}</span> : <span />}
        {r.review_uri && (
          <a href={r.review_uri} target="_blank" rel="noreferrer"
             className="text-ardoise/55 hover:text-domaine transition-colors whitespace-nowrap">
            Voir sur Google
          </a>
        )}
      </div>
    </article>
  );
}

export default function GoogleReviewsSection({ title = "Ils ont vécu la Briouse", subtitle = "", compact = false }) {
  const [data, setData] = React.useState(null);
  const [state, setState] = React.useState("idle");
  const sentinelRef = React.useRef(null);
  const scrollerRef = React.useRef(null);

  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      setState("loading");
      fetch(`${API}/api/public/google-reviews`)
        .then((r) => { if (!r.ok) throw new Error("unavailable"); return r.json(); })
        .then((d) => { setData(d); setState("ready"); })
        .catch(() => setState("error"));
    }, { rootMargin: "600px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("article");
    el.scrollBy({ left: dir * ((card ? card.offsetWidth : 320) + 24), behavior: "smooth" });
  };

  if (state === "error") return null;

  const ratingFr = data?.rating != null
    ? data.rating.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : null;

  return (
    <section ref={sentinelRef} id="avis-google" className={`${compact ? "py-14 md:py-16" : "py-20 md:py-28"} scroll-mt-24`} data-testid="google-reviews-section">
      <div className="text-center mb-10 md:mb-12">
        <p className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.3em] text-or mb-3">
          <GoogleLogo size={15} /> Avis Google
        </p>
        <h2 className="font-serif text-3xl md:text-4xl text-domaine">{title}</h2>
        {subtitle && <p className="mt-3 text-ardoise/70 max-w-2xl mx-auto">{subtitle}</p>}
        {state === "ready" && ratingFr && (
          <div className="mt-5 flex flex-col items-center gap-2">
            <Stars value={data.rating} size={20} />
            <p className="text-sm text-ardoise/80" data-testid="google-reviews-score">
              <strong className="text-domaine" data-testid="google-reviews-rating">{ratingFr}/5</strong> sur Google
              {data.user_rating_count != null && (
                <> — <span data-testid="google-reviews-count">{data.user_rating_count} avis</span></>
              )}
            </p>
          </div>
        )}
      </div>

      {state !== "ready" ? (
        <div className="flex gap-6 overflow-hidden" aria-hidden="true" data-testid="google-reviews-skeleton">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`shrink-0 w-[85%] md:w-[calc((100%-4rem)/3)] h-64 bg-pierre/25 animate-pulse ${i > 0 ? "hidden md:block" : ""}`} />
          ))}
        </div>
      ) : (
        <div className="relative">
          <div ref={scrollerRef}
               className="flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory pb-3 scrollbar-thin"
               data-testid="google-reviews-scroller">
            {(compact ? data.reviews.slice(0, 3) : data.reviews).map((r, i) => <ReviewCard key={`gr-${i}`} r={r} index={i} />)}
          </div>
          {!compact && data.reviews.length > 3 && (
            <div className="hidden md:flex absolute -top-14 right-0 gap-2">
              <button type="button" onClick={() => scrollBy(-1)} aria-label="Avis précédents"
                      data-testid="google-reviews-prev"
                      className="h-9 w-9 border border-pierre text-ardoise/70 hover:text-domaine hover:border-domaine transition-colors flex items-center justify-center">
                <ChevronLeft size={16} />
              </button>
              <button type="button" onClick={() => scrollBy(1)} aria-label="Avis suivants"
                      data-testid="google-reviews-next"
                      className="h-9 w-9 border border-pierre text-ardoise/70 hover:text-domaine hover:border-domaine transition-colors flex items-center justify-center">
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
        {state === "ready" && data.google_maps_uri && (
          <a href={data.google_maps_uri} target="_blank" rel="noreferrer"
             data-testid="google-reviews-see-all"
             className="w-full sm:w-auto text-center px-7 py-3 text-xs uppercase tracking-[0.2em] border border-domaine text-domaine hover:bg-domaine hover:text-lin transition-colors">
            Voir tous nos avis sur Google
          </a>
        )}
        {state === "ready" && data.write_review_uri && (
          <a href={data.write_review_uri} target="_blank" rel="noreferrer"
             data-testid="google-reviews-write"
             className="w-full sm:w-auto text-center px-7 py-3 text-xs uppercase tracking-[0.2em] bg-domaine text-lin hover:bg-or hover:text-domaine transition-colors">
            Laisser un avis
          </a>
        )}
      </div>

      <p className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-ardoise/50">
        <GoogleLogo size={12} /> Avis fournis par Google — classés par pertinence
      </p>
    </section>
  );
}
